/**
 * How the Sessions tab (TRA-1327) names a session's state and a tool call.
 * Pure, so the rules are testable without a DOM.
 */

import type { SessionStatus } from './transcript'

export const STATUS_BADGE: Record<
  SessionStatus,
  { label: string; variant: 'secondary' | 'warning' | 'outline' | 'destructive' }
> = {
  starting: { label: 'Starting', variant: 'secondary' },
  idle: { label: 'Idle', variant: 'outline' },
  working: { label: 'Working', variant: 'secondary' },
  approval: { label: 'Needs approval', variant: 'warning' },
  ended: { label: 'Ended', variant: 'outline' },
  failed: { label: 'Failed', variant: 'destructive' },
}

/** The input fields that say what a call does, in the order they are tried. */
const SUMMARY_FIELDS = ['command', 'file_path', 'path', 'pattern', 'set_id', 'run_id', 'url']

/** One line for a tool call: its command, path or pattern, else its input as JSON. */
export function toolSummary(input: unknown): string {
  if (input === null || input === undefined) return ''
  if (typeof input === 'object' && !Array.isArray(input)) {
    const fields = input as Record<string, unknown>
    for (const field of SUMMARY_FIELDS) {
      const value = fields[field]
      if (typeof value === 'string' && value !== '') return value
    }
  }
  return JSON.stringify(input)
}

/** The full input, for the expanded row and the approval card. */
export function toolInput(input: unknown): string {
  if (typeof input === 'object' && input !== null && !Array.isArray(input)) {
    const command = (input as Record<string, unknown>).command
    if (typeof command === 'string') return command
  }
  return JSON.stringify(input, null, 2)
}

export function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`
}

/** `Sep 25, 2026, 01:43 PM`, as BatchPage writes a date and time. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** `TrainLoop/trainloop-core@06d32ea4…` reads as `trainloop-core@06d32ea`. */
export function shortTrainerRef(ref: string): string {
  const [repository = ref, commit = ''] = ref.split('@')
  const name = repository.split('/').pop() ?? repository
  return commit === '' ? name : `${name}@${commit.slice(0, 7)}`
}
